import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ExitsInvestmentsToCompleteProfile } from '@shared/schemas';

import { issueCountReceived, issueCountStarted } from '../../_shared/store/issue-count.actions';
import { initialState } from './exits-investments-review.state';

/**
 * Exit & Investment Review slice (#762). Reducers are pure state transitions
 * only; the read lives in the saga, which dispatches these as it progresses.
 *
 * The badge has two sources that must never contradict the page: the list
 * just read (its length, exact and fresh) and the sidebar's count request,
 * heard through the shared `issueCount*` actions, which the Profile Cleaning
 * saga dispatches.
 */
const slice = createSlice({
  name: 'exitsInvestmentsReview',
  initialState,
  reducers: {
    loadStarted: (state) => {
      state.loadStatus = 'loading';
    },
    loadSucceeded: (state, action: PayloadAction<ExitsInvestmentsToCompleteProfile[]>) => {
      state.profiles = action.payload;
      state.loadStatus = 'loaded';
      // The badge equals the tile from now on.
      state.toCompleteCount = action.payload.length;
      // A count already on its way may have been made before this list.
      state.countInFlightPredatesList = true;
    },
    /** The list stays as it was, and so does the badge: an unread list is not an empty one. */
    loadFailed: (state) => {
      state.loadStatus = 'failed';
    },
    /**
     * The page was left (to a profile, say): its list is dropped, so coming
     * back shows the loading state rather than the old rows and figures for a
     * moment before the fresh read lands. The badge keeps its figure. The saga
     * also cancels a read still running.
     */
    pageLeft: (state) => {
      state.profiles = [];
      state.loadStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(issueCountStarted, (state) => {
        // Sent after the list: the server answers the list's size, or newer.
        state.countInFlightPredatesList = false;
      })
      .addCase(issueCountReceived, (state, action) => {
        const stale = state.countInFlightPredatesList;
        state.countInFlightPredatesList = false;
        if (stale) return;
        // Null: the server could not count the queue and had no size to give;
        // the last one read here stays, like after a failed request.
        if (action.payload.exits_investments_to_complete !== null) {
          state.toCompleteCount = action.payload.exits_investments_to_complete;
        }
      });
  },
});

export const exitsInvestmentsReviewActions = slice.actions;
export const exitsInvestmentsReviewReducer = slice.reducer;
